import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { IconX } from '@tabler/icons-react';
import 'mapbox-gl/dist/mapbox-gl.css';
import './RouteOverviewGlobeModal.css';

// Full-screen modal with a 3D rotatable Mapbox globe centred on the route.
// Opened from RouteOverviewMinimap when the user clicks it. Gives planetary
// context — "where on Earth is the Continental Divide / Annapurna Circuit".
//
// Props:
//   coordinates: [lng, lat][] — full route polyline
//   theme: { primary, secondary, primaryDark, ... }
//   switchCheckpointIndex: number | null — split point for the line colour
//   routeTitle: string
//   routeDistanceKm: number
//   startLabel: string
//   endLabel: string
//   onClose: () => void
export default function RouteOverviewGlobeModal({
  coordinates,
  theme,
  switchCheckpointIndex,
  routeTitle,
  routeDistanceKm,
  startLabel,
  endLabel,
  onClose,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  // Escape key closes the modal.
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Init the globe Mapbox instance.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (!coordinates || coordinates.length < 2) return;

    // Geographic centroid for initial focus.
    const sum = coordinates.reduce((a, c) => [a[0] + c[0], a[1] + c[1]], [0, 0]);
    const centroid = [sum[0] / coordinates.length, sum[1] / coordinates.length];

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      projection: 'globe',
      center: centroid,
      zoom: 1.4,
      interactive: true,
      attributionControl: true,
    });
    mapRef.current = map;

    map.on('style.load', () => {
      // Subtle atmosphere for the globe.
      try {
        map.setFog({
          color: 'rgba(255,255,255,0.85)',
          'high-color': '#9bbcf5',
          'horizon-blend': 0.04,
          'space-color': '#0b1424',
          'star-intensity': 0.15,
        });
      } catch (e) { /* setFog is fine to skip on style errors */ }
    });

    map.on('load', () => {
      const switchIdx =
        Number.isInteger(switchCheckpointIndex) &&
        switchCheckpointIndex > 0 &&
        switchCheckpointIndex < coordinates.length
          ? switchCheckpointIndex
          : null;

      if (switchIdx) {
        const pre = coordinates.slice(0, switchIdx + 1);
        const post = coordinates.slice(switchIdx);
        map.addSource('route-pre', {
          type: 'geojson',
          data: { type: 'Feature', geometry: { type: 'LineString', coordinates: pre } },
        });
        map.addSource('route-post', {
          type: 'geojson',
          data: { type: 'Feature', geometry: { type: 'LineString', coordinates: post } },
        });
        map.addLayer({
          id: 'route-pre',
          type: 'line',
          source: 'route-pre',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': theme.primary, 'line-width': 4 },
        });
        map.addLayer({
          id: 'route-post',
          type: 'line',
          source: 'route-post',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': theme.secondary, 'line-width': 4 },
        });
      } else {
        map.addSource('route', {
          type: 'geojson',
          data: { type: 'Feature', geometry: { type: 'LineString', coordinates } },
        });
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': theme.primary, 'line-width': 4 },
        });
      }

      // Start / end labelled pins.
      const makePin = (label) => {
        const el = document.createElement('div');
        el.className = 'route-globe-pin';
        el.textContent = label;
        el.style.background = theme.primary;
        return el;
      };
      const startMarker = new mapboxgl.Marker({ element: makePin(startLabel), anchor: 'bottom' })
        .setLngLat(coordinates[0])
        .addTo(map);
      const endMarker = new mapboxgl.Marker({ element: makePin(endLabel), anchor: 'bottom' })
        .setLngLat(coordinates[coordinates.length - 1])
        .addTo(map);
      markersRef.current = [startMarker, endMarker];

      // Compute the route's bounding box once for fitBounds.
      const routeBounds = coordinates.reduce(
        (b, c) => b.extend(c),
        new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
      );

      // Padding sized as a percentage of the canvas so the route consistently
      // fills ~65% of the modal width regardless of viewport size. Top gets
      // extra room for the header chip and the start/end pin labels.
      const canvas = map.getCanvas();
      const w = canvas.clientWidth || 1200;
      const h = canvas.clientHeight || 700;
      const padX = Math.max(80, Math.round(w * 0.175));
      const padY = Math.max(80, Math.round(h * 0.18));

      // Animate from the planetary globe down to the fitted route over 5s,
      // unless the user prefers reduced motion (jump straight there).
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      map.fitBounds(routeBounds, {
        padding: { top: padY + 40, bottom: padY, left: padX, right: padX },
        duration: reduced ? 0 : 5000,
        essential: true,
      });
    });

    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [coordinates, theme.primary, theme.secondary, switchCheckpointIndex, startLabel, endLabel]);

  return (
    <div className="join-modal-overlay route-globe-overlay" onClick={onClose}>
      <div className="route-globe-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <IconX size={20} />
        </button>
        <div className="route-globe-header">
          <h3>{routeTitle}</h3>
          {routeDistanceKm ? <span>{routeDistanceKm} km</span> : null}
        </div>
        <div ref={containerRef} className="route-globe-canvas" />
      </div>
    </div>
  );
}
